'use client';

import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import Loading from '../core/Loading';
import { Box, Typography, Button, Card, CardContent, Tab, Tabs } from '@mui/material';
import { formatAddress } from '@/stack-shared/utils/gen-utils';
import TitleBar from '../layout/TitleBar';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import { US_STATES } from '@/constants/form.constants';
import EditIcon from '@mui/icons-material/Edit';
import MapIcon from '@mui/icons-material/Map';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import {
  useInventoryAuditorLocationDetails,
  useUpdateInventoryAuditorLocation,
} from '@/hooks/useInventoryAuditorLocation';
import { useNotification } from '@/contexts/NotificationContext';
import InventoryAuditorLocationInventoryPanel from '../inventory-auditor/InventoryAuditorLocationInventoryPanel';
import InventoryAuditorLocationCountHistoryPanel from '../inventory-auditor/InventoryAuditorLocationCountHistoryPanel';

const InventoryAuditorLocationDetailPage: React.FC = () => {
  const { locationId } = useParams();
  const [searchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(false);
  const tabParam = searchParams.get('tab');
  const initialTab: 'details' | 'inventory' | 'history' =
    tabParam === 'history' || tabParam === 'inventory' ? tabParam : 'details';
  const [detailTab, setDetailTab] = useState<'details' | 'inventory' | 'history'>(initialTab);
  const initialCountDate = searchParams.get('countDate') ?? undefined;

  useEffect(() => {
    if (tabParam === 'history' || tabParam === 'inventory') {
      setDetailTab(tabParam);
    }
  }, [tabParam]);
  const { data, isLoading, error } = useInventoryAuditorLocationDetails(locationId);
  const updateMutation = useUpdateInventoryAuditorLocation();
  const { showSuccess, showError } = useNotification();

  const location = data?.location;

  if (isLoading) {
    return <Loading loading={true} noBackdrop />;
  }
  if (error || !location) {
    return <div>Error loading location</div>;
  }

  const locationFields = [
    {
      groupName: 'Location',
      fields: [
        { label: 'Location Name', name: 'name', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
        { label: 'Active', name: 'active', type: GENERIC_FORM_FIELD_TYPE.SWITCH, required: false, columns: 12 },
        { label: 'Address Line 1', name: 'addressLine1', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        { label: 'Address Line 2', name: 'addressLine2', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        { label: 'City', name: 'city', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
        {
          label: 'State',
          name: 'subdivision',
          type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
          required: false,
          columns: 6,
          options: US_STATES,
        },
        { label: 'Zip Code', name: 'postalCode', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
        { label: 'Country', name: 'country', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
      ],
    },
  ];

  const getGoogleMapsUrl = () => {
    const addressParts = [
      location.addressLine1,
      location.addressLine2,
      location.city,
      location.subdivision,
      location.postalCode,
      location.country,
    ].filter(Boolean);
    if (addressParts.length === 0) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts.join(', '))}`;
  };

  const handleUpdate = (values: any) => {
    const { id: _omit, ...rest } = values;
    updateMutation.mutate(
      { id: location.id, ...rest },
      {
        onSuccess: () => {
          setEditOpen(false);
          showSuccess('Location updated');
        },
        onError: () => showError('Failed to update location (admin role may be required)'),
      },
    );
  };

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Locations', href: '/app/inventory-auditor/locations' },
    { label: location.name },
  ];

  const addressForFormat = {
    addressLine1: location.addressLine1,
    addressLine2: location.addressLine2,
    city: location.city,
    subdivision: location.subdivision,
    postalCode: location.postalCode,
    country: location.country,
  };

  return (
    <>
      <TitleBar breadcrumbs={breadcrumbs} title={location.name} />
      <Box sx={{ p: 2 }}>
        <Tabs
          value={detailTab}
          onChange={(_, v: 'details' | 'inventory' | 'history') => setDetailTab(v)}
          sx={{ mb: 2, minHeight: 42, '& .MuiTab-root': { textTransform: 'none', minHeight: 42 } }}
        >
          <Tab label="Details" value="details" />
          <Tab label="Inventory" value="inventory" />
          <Tab label="Count history" value="history" />
        </Tabs>
        <GenericFormModal
          visible={editOpen}
          onDismiss={() => setEditOpen(false)}
          onSubmit={handleUpdate}
          initialValues={{ ...location }}
          fieldGroups={locationFields}
          title="Edit location"
        />
        {detailTab === 'details' ? (
          <Box sx={{ width: { xs: '100%', lg: '50%' }, mb: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {location.active === false ? 'Inactive' : 'Active'}
                </Typography>
                {formatAddress(addressForFormat) ? (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatAddress(addressForFormat)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No address set
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setEditOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit location
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FactCheckIcon />}
                    component={RouterLink}
                    to={`/app/inventory-auditor/locations/${location.id}/site-visit`}
                    sx={{ textTransform: 'none' }}
                  >
                    Full store count
                  </Button>
                  {getGoogleMapsUrl() && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<MapIcon />}
                      component="a"
                      href={getGoogleMapsUrl()!}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      Maps
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ) : detailTab === 'inventory' ? (
          <InventoryAuditorLocationInventoryPanel locationId={location.id} />
        ) : (
          <InventoryAuditorLocationCountHistoryPanel
            locationId={location.id}
            locationName={location.name ?? ''}
            initialCountDate={initialCountDate}
          />
        )}
      </Box>
    </>
  );
};

export default InventoryAuditorLocationDetailPage;
